import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import WorkTemplateTree from '@atom/components/common/workTemplateTree/WorkTemplateTree';
import { GET_WORK_ORDER_TEMPLATES } from '@atom/graph/workTemplate';
import { Button, Modal, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { PolicyAction } from '@atom/types/policy';

import classes from './filterPanel.module.css';

const styles = {
  button: {
    minWidth: 0,
    padding: 0,
    fontSize: fonts.sm,
    fontWeight: 'normal',
    textTransform: 'capitalize',
  },
  progress: {
    justifyContent: 'flex-start',
  },
  modalContent: {
    height: '50vh',
    padding: '2rem',
  },
};

type WorkTemplate = {
  id: string;
  name: string;
};

type WorkTemplateFilterProps = {
  workTemplateIds: Set<string>;
  toggled?: boolean;
  onWorkTemplatesUpdate: (ids: Set<string>) => void;
};

const WorkTemplateFilter = ({
  workTemplateIds,
  toggled,
  onWorkTemplatesUpdate,
}: WorkTemplateFilterProps) => {
  const [selected, setSelected] = useState<WorkTemplate[]>([]);
  const [applied, setApplied] = useState<WorkTemplate[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  const [getWorkTemplates, { loading }] = useLazyQuery(
    GET_WORK_ORDER_TEMPLATES,
  );

  useEffect(() => {
    if (workTemplateIds.size === 0) {
      return;
    }

    const fetchTemplates = async () => {
      try {
        const { data } = await getWorkTemplates({
          variables: {
            input: {
              ids: Array.from(workTemplateIds),
              action: PolicyAction.READ,
            },
          },
        });

        const templates = data?.workOrderTemplates?.workOrderTemplates ?? [];
        setSelected(templates);
        setApplied(templates);
      } catch {
        return;
      }
    };

    fetchTemplates();
  }, [workTemplateIds]);

  useEffect(() => {
    const workTemplates = selected.filter(({ id }) => workTemplateIds.has(id));

    setSelected(workTemplates);
    setApplied(workTemplates);
  }, [workTemplateIds]);

  const handleToggle = (template?: WorkTemplate) => {
    const isSelected = selected.some(({ id }) => template.id === id);

    setSelected(
      isSelected
        ? selected.filter(({ id }) => id !== template.id)
        : [...selected, template],
    );
  };

  const handleSave = () => {
    const selectedWorkTemplateIds = selected.map(({ id }) => id);

    onWorkTemplatesUpdate(new Set(selectedWorkTemplateIds));
    setApplied(selected);
    setOpen(false);
  };

  const handleReset = () => {
    onWorkTemplatesUpdate(new Set([]));
  };

  const handleClear = () => setSelected([]);

  const handleCancel = () => {
    setSelected(applied);
    setOpen(false);
  };

  const selectedIds = new Set(selected.map(({ id }) => id));
  const workTemplateDisplay = applied
    .filter(({ id }) => workTemplateIds.has(id))
    .map(({ name }) => name)
    .join(', ');

  const buttonStyles = {
    ...styles.button,
    color: toggled ? colors.brand.blue : colors.neutral.gray,
  };

  return (
    <>
      <div className={classes.filterContainerLabel}>Work Template</div>
      <div className={classes.buttonContainer}>
        <Button
          style={buttonStyles}
          size="small"
          onClick={() => setOpen(true)}
          disabled={loading || !toggled}
          data-cy="mapFilterTemplateSelect"
        >
          select
        </Button>
        {workTemplateIds.size > 0 && (
          <>
            <span className={classes.buttonDivider}>|</span>
            <Button
              style={{ ...buttonStyles, marginRight: '1rem' }}
              size="small"
              disabled={loading || !toggled}
              onClick={handleReset}
              data-cy="mapFilterTemplateClear"
            >
              clear all
            </Button>
          </>
        )}
      </div>
      <div className={classes.filterValues}>
        {loading && <Progress size={20} style={styles.progress} />}
        {workTemplateDisplay}
      </div>
      <Modal
        title="Select Work Template"
        open={open}
        onCancel={handleCancel}
        onConfirm={handleSave}
        onClear={handleClear}
        contentStyle={styles.modalContent}
        confirmButtonText="Ok"
      >
        <WorkTemplateTree
          includeSearch
          getTemplateSelected={template => selectedIds.has(template.id)}
          onTemplateToggle={template => handleToggle(template)}
        />
      </Modal>
    </>
  );
};

export default WorkTemplateFilter;
